// PDF de cotización con branding NULED.
// Modos: cliente (PVP) | interno (con costo + margen) via ?modo=interno
//
// Diseño: header oscuro #0e0e0e + accent verde #57FF9A, logo SVG inline,
// productos con foto a la izquierda, condiciones detalladas.

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { thumbUrl } from '../lib/imageUrl';
import { lutronSpecUrl } from '../lib/lutronSpec';
import { theme } from '../styles/theme';

type Quote = {
  id: string;
  quote_number: string;
  customer_id: string;
  currency: string;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  status: string;
  kind: string;
  markup_pct: number | null;
  valid_until: string | null;
  payment_terms: string | null;
  delivery_terms: string | null;
  estimated_ready_date: string | null;
  notes: string | null;
  project_title?: string | null;
  created_at: string;
  customer?: {
    business_name: string;
    legal_name: string | null;
    rfc: string | null;
    primary_email: string | null;
    primary_phone: string | null;
  };
};

type QuoteItem = {
  id: string;
  product_id: string | null;
  sku: string;
  description: string;
  brand_code: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_pct: number | null;
  line_total: number;
  notes: string | null;
  // From join
  image_url?: string | null;
  msrp_usd_canonical?: number | null;
};

// Paleta NULED (de theme.ts — verde petróleo + amarillo lima)
const BRAND = theme.brand;           // '#2D484F' verde petróleo
const ACCENT = theme.accent;         // '#CCCC33' amarillo lima
const ACCENT_DARK = theme.accentDark; // '#A8A82A'
const TEXT = theme.text;             // '#2D484F'
const MUTED = theme.textMuted;       // '#6B7B7F'
const BORDER = theme.border;         // '#EDEDED'

function NuledLogo({ height = 32, color = '#FFFFFF' }: { height?: number; color?: string }) {
  return (
    <svg viewBox="0 0 245.417 58.032" height={height} style={{ display: 'block', color }} aria-label="NULED">
      <path d="M3.289,0,32.517,56.97l.126.247H36.6L7.244,0Z" transform="translate(1.483)" fill="currentColor" />
      <rect width="3.517" height="57.216" transform="translate(0 0)" fill="currentColor" />
      <rect width="3.519" height="57.216" transform="translate(39.336 0)" fill="currentColor" />
      <path d="M77.875,0V35.545c0,12.339-6.013,19.134-16.93,19.134s-16.93-6.795-16.93-19.134V0H40.5V33.995c0,15.726,7.071,24.037,20.447,24.037s20.447-8.311,20.447-24.037V0Z" transform="translate(18.264 -0.001)" fill="currentColor" />
      <path d="M83.163,53.863V0H79.646V57.216h32V53.863Z" transform="translate(35.919 -0.001)" fill="currentColor" />
      <path d="M111.231,53.863V29.143h27.421V25.789H111.231V3.354h28.155V0H107.712V57.216h32.326V53.863Z" transform="translate(48.576 -0.001)" fill="currentColor" />
      <path d="M152,0H138.649V57.216H152c19.343,0,30.893-10.694,30.893-28.607S171.34,0,152,0m27.212,28.609c0,16.521-9.693,25.254-28.029,25.254h-9.013V3.355h9.013c18.336,0,28.029,8.732,28.029,25.254" transform="translate(62.528 -0.001)" fill="currentColor" />
    </svg>
  );
}

export function QuotePdf() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const modo = params.get('modo') === 'interno' ? 'internal' : 'client';
  const [quote, setQuote] = useState<Quote | null>(null);
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);

      const { data: q } = await supabase.from('quotes')
        .select('*, customer:customers(business_name, legal_name, rfc, primary_email, primary_phone)')
        .eq('id', id).maybeSingle();

      // Items: campos correctos (description no name, quantity no qty)
      // + JOIN con products para traer foto + msrp_usd canonical (para calcular margen interno)
      const { data: itemsData } = await supabase.from('quote_items')
        .select(`
          id, product_id, sku, description, brand_code, quantity, unit, unit_price,
          discount_pct, line_total, notes, sort_order,
          product:products(images, msrp_usd)
        `)
        .eq('quote_id', id)
        .order('sort_order');

      const enrichedItems: QuoteItem[] = ((itemsData as any[]) ?? []).map(it => {
        const img = it.product?.images?.find((i: any) => i?.is_thumbnail) ?? it.product?.images?.[0];
        return {
          id: it.id,
          product_id: it.product_id,
          sku: it.sku,
          description: it.description,
          brand_code: it.brand_code,
          quantity: Number(it.quantity),
          unit: it.unit,
          unit_price: Number(it.unit_price),
          discount_pct: it.discount_pct != null ? Number(it.discount_pct) : null,
          line_total: Number(it.line_total),
          notes: it.notes,
          image_url: img?.url ?? null,
          msrp_usd_canonical: it.product?.msrp_usd ? Number(it.product.msrp_usd) : null,
        };
      });

      if (q) setQuote(q as any);
      setItems(enrichedItems);
      setLoading(false);
    })();
  }, [id]);

  if (loading) return <div style={{ padding: 40, fontFamily: 'Roboto, Helvetica, Arial, sans-serif' }}>Cargando…</div>;
  if (!quote) return <div style={{ padding: 40, fontFamily: 'Roboto, Helvetica, Arial, sans-serif' }}>Cotización no encontrada</div>;

  const subtotal = items.reduce((s, it) => s + it.line_total, 0);
  // Costo interno = unit_price del item ÷ (1 - discount_pct/100) reverso, o usar msrp_usd_canonical si está
  // Más limpio: para modo interno, costo = unit_price (porque la quote interna ya tiene costo como unit_price si es kind=to_nuled)
  // En modo cliente la quote tiene unit_price = costo × (1+margen), pero el costo real es:
  //   unit_price / (1 + markup_pct/100)
  const markupPct = Number(quote.markup_pct ?? 0);
  const isToClient = quote.kind === 'to_client';

  const lineCost = (it: QuoteItem): number => {
    if (!isToClient) return it.unit_price * it.quantity;  // ya es costo
    if (markupPct > 0) return (it.unit_price / (1 + markupPct / 100)) * it.quantity;
    return it.line_total;
  };
  const linePvp = (it: QuoteItem): number => {
    if (isToClient) return it.line_total;
    // Modo to_nuled: PVP es msrp_usd_canonical × qty
    if (it.msrp_usd_canonical) return it.msrp_usd_canonical * it.quantity;
    return it.line_total;
  };

  const totalCost = items.reduce((s, it) => s + lineCost(it), 0);
  const totalPvp = items.reduce((s, it) => s + linePvp(it), 0);
  const margin = totalPvp - totalCost;
  const marginPct = totalPvp > 0 ? (margin / totalPvp) * 100 : 0;
  const iva = subtotal * 0.16;
  const grandTotal = subtotal + iva;

  // Vista cliente: precio público total + descuento total (suma de la columna P. público × qty)
  const pvpTotalCliente = items.reduce((s, it) => {
    const qty = it.quantity || 1;
    const finalUnit = it.line_total / qty;
    let pvp: number;
    if (it.discount_pct && it.discount_pct > 0) pvp = it.unit_price;
    else if (it.msrp_usd_canonical && it.msrp_usd_canonical > finalUnit) pvp = it.msrp_usd_canonical;
    else if (it.unit_price > finalUnit) pvp = it.unit_price;
    else pvp = finalUnit;
    return s + pvp * qty;
  }, 0);
  const ahorroCliente = pvpTotalCliente - subtotal;
  const ahorroPctCliente = pvpTotalCliente > 0 ? (ahorroCliente / pvpTotalCliente) * 100 : 0;

  const fmt = (n: number) => `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <>
      <style>{`
        /* CRITICAL: forzar que Chrome imprima los backgrounds (header oscuro, accent, tabla) */
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        @media print {
          @page { size: letter; margin: 0; }
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          /* Imprimir SOLO la cotización: oculta los widgets flotantes globales (WhatsApp, foco, chat) que tapaban el total */
          body * { visibility: hidden !important; }
          .pdf-wrap, .pdf-wrap * { visibility: visible !important; }
          .no-print, .no-print * { visibility: hidden !important; display: none !important; }
          .pdf-wrap { box-shadow: none !important; max-width: none !important; margin: 0 !important; }
          /* Romper página si el contenido es muy largo, no cortar filas */
          tr, .totals-row { page-break-inside: avoid; }
        }
        body { margin: 0; background: #f5f5f5; font-family: Roboto, Helvetica, Arial, sans-serif; }
        .pdf-wrap {
          max-width: 8.5in;
          margin: 0 auto;
          padding: 0;
          background: white;
          color: ${TEXT};
          font-family: Roboto, Helvetica, Arial, sans-serif;
          font-size: 11px;
          line-height: 1.4;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        }
        .pdf-content { padding: 0.45in; }
        .pdf-content h1 { margin: 0; font-size: 22px; font-weight: 300; color: ${BRAND}; letter-spacing: -0.5px; }
        .pdf-content table { width: 100%; border-collapse: collapse; margin-top: 18px; }
        .pdf-content th { padding: 9px 8px; text-align: left; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; }
        .pdf-content td { padding: 10px 8px; border-bottom: 1px solid ${BORDER}; vertical-align: top; }
      `}</style>

      <div className="no-print" style={{
        background: BRAND, color: 'white', padding: '14px 24px',
        display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between',
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <NuledLogo height={20} color="white" />
          <span style={{ fontSize: 11, opacity: 0.7 }}>
            {modo === 'internal' ? 'Vista interna · con costos' : 'Vista cliente · solo PVP'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => window.print()} style={{
            background: ACCENT, color: BRAND, border: 'none',
            padding: '8px 18px', fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer',
            fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          }}>📥 Descargar / Imprimir</button>
          <a href={modo === 'internal' ? `?` : `?modo=interno`} style={{
            background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.3)',
            padding: '8px 18px', fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.5, textDecoration: 'none',
            fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          }}>
            {modo === 'internal' ? 'Ver vista cliente' : 'Ver vista interna'}
          </a>
        </div>
      </div>

      <div className="pdf-wrap">
        {/* HEADER con branding oscuro */}
        <div style={{
          background: BRAND,
          color: 'white',
          padding: '0.45in 0.45in 0.3in',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}>
          <div>
            <NuledLogo height={32} color="white" />
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.65)', marginTop: 10, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 600 }}>
              Iluminación LED · Mayorista Lutron
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.85)', marginTop: 6 }}>
              nuled.mx · ventas@nuled.mx · WhatsApp 55-0000-0000
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: ACCENT,
              textTransform: 'uppercase', letterSpacing: 2,
            }}>
              Cotización
            </div>
            <div style={{ fontFamily: 'Courier, monospace', fontSize: 16, fontWeight: 700, marginTop: 4 }}>
              {quote.quote_number}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', marginTop: 8 }}>
              Emitida: {new Date(quote.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}
            </div>
            {quote.valid_until && (
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>
                Válida hasta: {new Date(quote.valid_until).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}
              </div>
            )}
          </div>
        </div>

        {/* Accent strip */}
        <div style={{ height: 4, background: ACCENT }} />

        <div className="pdf-content">
          {/* CLIENTE */}
          <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, color: MUTED, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700, marginBottom: 6 }}>
                Cotizado a
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: BRAND }}>
                {quote.customer?.business_name ?? '—'}
              </div>
              {quote.customer?.legal_name && quote.customer.legal_name !== quote.customer.business_name && (
                <div style={{ fontSize: 11, color: TEXT, marginTop: 2 }}>{quote.customer.legal_name}</div>
              )}
              {quote.customer?.rfc && (
                <div style={{ fontFamily: 'Courier, monospace', fontSize: 10, marginTop: 4, color: TEXT }}>
                  RFC: {quote.customer.rfc}
                </div>
              )}
              {quote.customer?.primary_email && (
                <div style={{ fontSize: 10, marginTop: 2, color: TEXT }}>{quote.customer.primary_email}</div>
              )}
              {quote.customer?.primary_phone && (
                <div style={{ fontSize: 10, marginTop: 2, color: TEXT }}>Tel: {quote.customer.primary_phone}</div>
              )}
              {quote.project_title && (
                <div style={{ fontSize: 11, marginTop: 8, color: TEXT, fontStyle: 'italic' }}>
                  Proyecto: <strong>{quote.project_title}</strong>
                </div>
              )}
            </div>
            {modo === 'internal' && (
              <div style={{
                padding: '8px 14px',
                background: '#FEF3C7',
                border: '2px solid #F59E0B',
                fontSize: 10, color: BRAND, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.5,
                alignSelf: 'flex-start', maxWidth: 220, lineHeight: 1.3,
              }}>
                ⚠️ Solo uso interno<br />
                <span style={{ fontSize: 9, fontWeight: 500, textTransform: 'none' }}>
                  No enviar al cliente final. Contiene costos.
                </span>
              </div>
            )}
          </div>

          {/* TABLE */}
          <table>
            <thead>
              <tr style={{ background: BRAND, color: 'white' }}>
                <th style={{ width: 48 }}></th>
                <th style={{ width: 110 }}>SKU</th>
                <th>Producto</th>
                <th style={{ textAlign: 'center', width: 55 }}>Cant.</th>
                {modo === 'internal' ? (
                  <>
                    <th style={{ textAlign: 'right', width: 75 }}>Tu costo</th>
                    <th style={{ textAlign: 'right', width: 80 }}>PVP unit.</th>
                    <th style={{ textAlign: 'right', width: 70 }}>Margen</th>
                    <th style={{ textAlign: 'right', width: 90 }}>Subtotal</th>
                  </>
                ) : (
                  <>
                    <th style={{ textAlign: 'right', width: 80 }}>P. público</th>
                    <th style={{ textAlign: 'center', width: 55 }}>Desc.</th>
                    <th style={{ textAlign: 'right', width: 80 }}>P. final</th>
                    <th style={{ textAlign: 'right', width: 90 }}>Subtotal</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={modo === 'internal' ? 8 : 8} style={{ textAlign: 'center', padding: 30, color: MUTED }}>
                  Sin items
                </td></tr>
              ) : items.map(it => {
                const itemCost = lineCost(it);
                const itemPvp = linePvp(it);
                const itemMargin = itemPvp - itemCost;
                // Para vista cliente: derivar PVP / descuento / precio final
                // unit_price puede ser PVP (con discount_pct separado) o ya precio neto.
                // Si discount_pct > 0, entonces unit_price es PVP y precio_final = unit_price × (1 - desc/100).
                // Si discount_pct = 0 pero msrp_usd_canonical > unit_price, derivamos desde MSRP.
                const qty = it.quantity || 1;
                const finalUnit = it.line_total / qty;
                let pvpUnit: number;
                let descPct: number;
                if (it.discount_pct && it.discount_pct > 0) {
                  // Caso A: la cotización guardó PVP en unit_price y % por separado
                  pvpUnit = it.unit_price;
                  descPct = it.discount_pct;
                } else if (it.msrp_usd_canonical && it.msrp_usd_canonical > finalUnit) {
                  // Caso B: usar MSRP del catálogo como PVP
                  pvpUnit = it.msrp_usd_canonical;
                  descPct = (1 - finalUnit / pvpUnit) * 100;
                } else if (it.unit_price > finalUnit) {
                  // Caso C: unit_price es PVP, line_total trae descuento
                  pvpUnit = it.unit_price;
                  descPct = (1 - finalUnit / pvpUnit) * 100;
                } else {
                  // Caso D: sin descuento — PVP = precio final
                  pvpUnit = finalUnit;
                  descPct = 0;
                }
                return (
                  <tr key={it.id}>
                    <td>
                      {it.image_url ? (
                        <img src={thumbUrl(it.image_url, 80)} alt="" style={{
                          width: 40, height: 40, objectFit: 'contain',
                          background: '#FAFAFA', border: `1px solid ${BORDER}`,
                        }} />
                      ) : (
                        <div style={{
                          width: 40, height: 40,
                          background: '#FAFAFA', border: `1px solid ${BORDER}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 16, color: MUTED,
                        }}>💡</div>
                      )}
                    </td>
                    <td>
                      <span style={{ fontFamily: 'Courier, monospace', fontSize: 10, fontWeight: 700 }}>{it.sku}</span>
                      <div style={{ fontSize: 8, color: MUTED, textTransform: 'uppercase', marginTop: 2, letterSpacing: 0.5 }}>
                        {it.brand_code}
                      </div>
                      {it.brand_code === 'lutron' && lutronSpecUrl(it.sku) && (
                        <a
                          href={lutronSpecUrl(it.sku)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="no-print"
                          style={{
                            display: 'inline-block', marginTop: 4,
                            fontSize: 8, color: ACCENT_DARK, fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: 0.5,
                            textDecoration: 'none', borderBottom: `1px solid ${ACCENT}`,
                          }}
                        >
                          Ficha técnica ↗
                        </a>
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 11, color: BRAND }}>{it.description}</div>
                      {it.notes && (
                        <div style={{ fontSize: 9, color: MUTED, fontStyle: 'italic', marginTop: 2 }}>
                          {it.notes}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 700 }}>
                      {it.quantity.toLocaleString('es-MX', { maximumFractionDigits: 2 })}
                      <div style={{ fontSize: 8, color: MUTED }}>{it.unit}</div>
                    </td>

                    {modo === 'internal' ? (
                      <>
                        <td style={{ textAlign: 'right', color: MUTED, fontSize: 10 }}>
                          {fmt(itemCost / it.quantity)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {fmt(it.unit_price)}
                        </td>
                        <td style={{ textAlign: 'right', color: itemMargin > 0 ? ACCENT_DARK : MUTED, fontWeight: 700 }}>
                          {fmt(itemMargin)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: BRAND }}>
                          {fmt(it.line_total)}
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ textAlign: 'right', color: MUTED, fontSize: 10 }}>
                          {/* P. público con tachado si hay descuento */}
                          {descPct > 0 ? (
                            <span style={{ textDecoration: 'line-through' }}>{fmt(pvpUnit)}</span>
                          ) : fmt(pvpUnit)}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: descPct > 0 ? ACCENT_DARK : MUTED }}>
                          {descPct > 0 ? `-${descPct.toFixed(1)}%` : '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {fmt(finalUnit)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: BRAND }}>
                          {fmt(it.line_total)}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* TOTALS */}
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: 320 }}>
              {modo === 'internal' && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 11, color: MUTED }}>
                    <span>Costo total</span><span>{fmt(totalCost)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 11, color: ACCENT_DARK, fontWeight: 700 }}>
                    <span>Margen ({marginPct.toFixed(1)}%)</span><span>{fmt(margin)}</span>
                  </div>
                  <div style={{ borderTop: '1px dashed ' + BORDER, margin: '8px 0' }} />
                </>
              )}
              {modo === 'client' && ahorroCliente > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 11, color: MUTED }}>
                    <span>Precio público</span>
                    <span style={{ textDecoration: 'line-through' }}>{fmt(pvpTotalCliente)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 11, color: ACCENT_DARK, fontWeight: 700 }}>
                    <span>Tu ahorro ({ahorroPctCliente.toFixed(1)}%)</span><span>-{fmt(ahorroCliente)}</span>
                  </div>
                  <div style={{ borderTop: '1px dashed ' + BORDER, margin: '8px 0' }} />
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                <span>Subtotal</span><span>{fmt(subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 11, color: MUTED }}>
                <span>IVA 16%</span><span>{fmt(iva)}</span>
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                borderTop: `3px solid ${BRAND}`,
                marginTop: 8, paddingTop: 10,
                fontSize: 18, fontWeight: 700, color: BRAND,
              }}>
                <span>TOTAL {quote.currency || 'USD'}</span>
                <span>{fmt(grandTotal)}</span>
              </div>
            </div>
          </div>

          {/* CONDICIONES Y TÉRMINOS */}
          <div style={{
            marginTop: 32,
            padding: 16,
            background: '#FAFAFA',
            border: `1px solid ${BORDER}`,
            borderLeft: `4px solid ${ACCENT}`,
          }}>
            <div style={{ fontSize: 10, color: BRAND, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 10 }}>
              Condiciones comerciales
            </div>
            <div style={{ fontSize: 10, lineHeight: 1.7, color: TEXT }}>
              <div style={{ marginBottom: 5 }}>
                <strong>Vigencia:</strong> {quote.valid_until
                  ? `Hasta el ${new Date(quote.valid_until).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}.`
                  : '15 días naturales desde la emisión.'}
                {' '}Precios sujetos a tipo de cambio y disponibilidad en stock al confirmar.
              </div>
              <div style={{ marginBottom: 5 }}>
                <strong>Pago:</strong> {quote.payment_terms ?? 'Transferencia bancaria a cuenta NULED. Anticipo del 50% para confirmar pedido, saldo contra entrega.'}
              </div>
              <div style={{ marginBottom: 5 }}>
                <strong>Entrega:</strong> {quote.delivery_terms ?? 'Stock NULED: 1-3 días hábiles CDMX. SKUs bajo pedido: 15-30 días hábiles (Lutron USA).'}
                {quote.estimated_ready_date && <> {' '}<strong>Entrega estimada:</strong> {new Date(quote.estimated_ready_date + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}.</>}
              </div>
              <div style={{ marginBottom: 5 }}>
                <strong>Envío:</strong> A cargo del cliente. Cotizamos guía con tu paquetería preferida (Estafeta, Fedex, DHL) o entrega directa en CDMX/Metropolitana.
              </div>
              <div style={{ marginBottom: 5 }}>
                <strong>Garantía NULED:</strong> 1 año contra defectos de fabricación en luminarias LED, a partir de la fecha de entrega. Se atiende mediante reparación o reposición del producto; no aplica reembolso en efectivo. No cubre daños por instalación indebida, sobretensión, uso distinto al especificado o desgaste normal.
              </div>
              <div style={{ marginBottom: 5 }}>
                <strong>Garantía Lutron:</strong> 1 año mecánica, 8 años circuitos electrónicos (consultar línea específica).
              </div>
              <div style={{ marginBottom: 5 }}>
                <strong>Factura:</strong> Se emite contra pago confirmado. Forma de pago según método. Uso de CFDI a definir.
              </div>
            </div>
          </div>

          {/* CAMBIOS Y DEVOLUCIONES (política estricta) */}
          <div style={{
            marginTop: 12,
            padding: 16,
            background: '#FFF7F5',
            border: `1px solid ${BORDER}`,
            borderLeft: `4px solid #B91C1C`,
          }}>
            <div style={{ fontSize: 10, color: BRAND, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 10 }}>
              Cambios y devoluciones
            </div>
            <div style={{ fontSize: 10, lineHeight: 1.7, color: TEXT }}>
              <div style={{ marginBottom: 6 }}>
                Todas las ventas de NULED son <strong>en firme</strong>. Por tratarse de material de iluminación especificado a proyecto, <strong>no se aceptan cambios ni devoluciones</strong>, salvo casos excepcionales autorizados <strong>por escrito</strong> por NULED.
              </div>
              <div style={{ marginBottom: 4 }}>• No aplican devoluciones en producto sobre pedido, fabricado a la medida, descontinuado, en promoción, ni instalado o energizado.</div>
              <div style={{ marginBottom: 4 }}>• Toda solicitud debe presentarse por escrito dentro de los <strong>5 días naturales</strong> posteriores a la recepción, conservando empaque original, accesorios y en condición de reventa.</div>
              <div style={{ marginBottom: 4 }}>• De aprobarse excepcionalmente, aplica una <strong>penalización mínima del 20%</strong> sobre el valor del producto por restitución de inventario (restocking), más flete, maniobras y, en su caso, diferencias cambiarias. El porcentaje puede ser mayor según el estado del producto.</div>
              <div style={{ marginBottom: 4 }}>• Producto con daño físico, sin empaque original o fuera del plazo <strong>no es sujeto</strong> de cambio ni devolución.</div>
              <div>• Cualquier reembolso aprobado se realiza en la <strong>misma moneda y forma de pago</strong> original; NULED no cubre diferencias por tipo de cambio.</div>
            </div>
          </div>

          {/* NOTAS */}
          {quote.notes && (
            <div style={{ marginTop: 16, fontSize: 11, color: TEXT, padding: '12px 14px', background: '#FFFEF7', border: `1px solid #F4E4A1` }}>
              <div style={{ fontSize: 9, color: BRAND, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                Notas
              </div>
              {quote.notes}
            </div>
          )}

          {/* FOOTER */}
          <div style={{ marginTop: 28, paddingTop: 14, borderTop: `1px solid ${BORDER}`, textAlign: 'center' }}>
            <NuledLogo height={14} color={BRAND} />
            <div style={{ fontSize: 8, color: MUTED, marginTop: 8, letterSpacing: 0.4 }}>
              {modo === 'client'
                ? 'NULED · Mayorista Lutron · nuled.mx · ventas@nuled.mx · Gracias por confiar en NULED.'
                : 'Cotización interna · cost basis para distribuidor. No incluir en envío al cliente final.'}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
